// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 63.24.0 Slice B — personnel eligibility evidence for a controlled-testing
 * case: the person a case names, the dated roles that person holds, the evidence
 * each role rests on, the qualification alternative the evidence shows, and the
 * State Bank's outcome of the criminal-record check.
 *
 * ## What is stored, and what is never stored
 *
 * Stored: the person's name as the person entering the record typed it, the
 * statement's signing date, the dated roles with the alternative a person recorded
 * for each and the evidence files it rests on, and the criminal-record OUTCOME (a
 * code, its date and the recorder). Nothing else.
 *
 * The person is a free name typed on each record, not a link to a user account and
 * not a lookup in any register (owner decision, 2026-10-11). The name is the only
 * identifier of the person, and the overlap rule compares names as typed, trimmed.
 *
 * Never stored: a currency flag, an applicability answer, an overlap answer, a
 * verdict, or any "pass" or "fail" word. Each is computed on read from the stored
 * record and the installed pack, so a pack change reaches every record on the
 * next read and no stored answer can disagree with the record it came from.
 *
 * The criminal-record check has no field for content. The platform records what
 * the State Bank reported, as a code from the installed vocabulary, and the
 * record itself stays with the authority that holds it.
 *
 * ## The pack's vocabulary (R-46)
 *
 * Roles, qualification alternatives and criminal-record outcomes are read from
 * the installed form templates that declare `register_surface:
 * CONTROLLED_TESTING_PERSONNEL_REGISTER_SURFACE` and bind to the case's
 * framework. The same reader the other registers use (`readPackFormsBySurface`,
 * `packFormOptions`) reads the options; nothing is spelled in `src/`. A framework
 * whose installed pack declares no option for a field gives an honest "not
 * declared" answer, and a record cannot take a value no installed pack declares.
 *
 * Each option may carry:
 * - `applies_from` / `applies_until` (`YYYY-MM-DD`, inclusive): the option is in
 *   force on those dates only (`packOptionInForce`);
 * - `applies_to_application_form_ids` (role options only): the application forms
 *   the pack says the role's test applies to. Absent means the pack has not said,
 *   and applicability reads as not declared. It is never assumed.
 */
/** Collection holding one document per personnel record. `record_id` is also `_id`. */
export const CONTROLLED_TESTING_PERSONNEL_COLLECTION = 'grc_controlled_testing_personnel';
/** The `register_surface` an installed pack form declares to feed this register's vocabularies. */
export const CONTROLLED_TESTING_PERSONNEL_REGISTER_SURFACE = 'controlled_testing_personnel';
/**
 * The field ids the surface's templates carry. These are platform field names,
 * the same kind of identifier as `DATA_SERVICE_PROVIDER_VOCABULARY_FIELDS`, and
 * name no instrument. The `options[]` under each id are the pack's declarations.
 */
export const CONTROLLED_TESTING_PERSONNEL_FIELDS = {
    role: 'role',
    qualificationBasis: 'qualification_basis',
    criminalRecordOutcome: 'criminal_record_outcome',
    /**
     * The two conditions on a P2P company's legal representative and general director
     * (owner decisions (a) and (b), 2026-10-11).
     */
    nationalityCondition: 'nationality_condition',
    /**
     * RETIRED at pack 1.0.8 (owner decision (b)): no declared option is read for it. The id
     * stays here so the citation validator still checks a copy of it if one reaches a pack.
     */
    convictionCondition: 'conviction_condition',
    sanctionCondition: 'sanction_condition',
};
/** The live condition fields, in the order the record shows them. */
export const CONTROLLED_TESTING_PERSONNEL_CONDITION_FIELDS = [
    CONTROLLED_TESTING_PERSONNEL_FIELDS.nationalityCondition,
    CONTROLLED_TESTING_PERSONNEL_FIELDS.sanctionCondition,
];
/** The option attribute a role uses to name the application forms its test applies to. */
export const CONTROLLED_TESTING_ROLE_APPLIES_TO_FORMS_KEY = 'applies_to_application_form_ids';
/**
 * The option attribute a condition uses to name the roles it applies to: a
 * condition is read for a person only when the record holds one of these roles.
 * Absent means the pack has not said, and the condition reads as not declared.
 */
export const CONTROLLED_TESTING_CONDITION_APPLIES_TO_ROLES_KEY = 'applies_to_role_codes';
/** The evidence owner type a personnel record's uploaded files are held under. */
export const CONTROLLED_TESTING_PERSONNEL_EVIDENCE_OWNER_TYPE = 'controlled_testing_personnel_evidence';
/** Error codes the personnel record returns. Stable, machine-readable names. */
export const CONTROLLED_TESTING_PERSONNEL_ERROR_CODES = {
    RECORD_NOT_FOUND: 'PERSONNEL_RECORD_NOT_FOUND',
    /** A role the installed pack does not declare for this framework. */
    ROLE_NOT_DECLARED: 'ROLE_NOT_DECLARED',
    /** A qualification alternative the installed pack does not declare. */
    QUALIFICATION_BASIS_NOT_DECLARED: 'QUALIFICATION_BASIS_NOT_DECLARED',
    /** The same person holds the same role over a period that overlaps a recorded one. */
    ROLE_PERIOD_OVERLAPS: 'ROLE_PERIOD_OVERLAPS',
    /** A role whose last day is before its first day. */
    ROLE_PERIOD_INVERTED: 'ROLE_PERIOD_INVERTED',
    /** A criminal-record outcome code the installed pack does not declare. */
    CRIMINAL_RECORD_OUTCOME_NOT_DECLARED: 'CRIMINAL_RECORD_OUTCOME_NOT_DECLARED',
    /** A condition option the installed pack does not declare for this condition. */
    CONDITION_NOT_DECLARED: 'PERSONNEL_CONDITION_NOT_DECLARED',
    CONDITION_EVIDENCE_REQUIRED: 'PERSONNEL_CONDITION_EVIDENCE_REQUIRED',
    LEGACY_CONDITION_READ_ONLY: 'PERSONNEL_LEGACY_CONDITION_READ_ONLY',
    /** A condition written on a case whose form does not carry it, or on a record that holds none of its roles. */
    CONDITION_NOT_APPLICABLE: 'PERSONNEL_CONDITION_NOT_APPLICABLE',
    /** A file a role names is not held for this record. Upload it against the record first. */
    EVIDENCE_NOT_OWNED: 'PERSONNEL_EVIDENCE_NOT_OWNED',
};
//# sourceMappingURL=controlled-testing-personnel.js.map